import { useState, useEffect } from 'react';
import {
  Repository,
  RepositoryHealthDetails,
  ProjectPipelineData,
  ProjectReleaseData,
  ProjectBranchPolicyReport,
  FilteredProjectDeploymentData,
  ProjectBranchNamingReport,
  PipelineInfo,
  Release,
  BranchPolicyCompliance,
  ProcessedDeployment,
  BranchNamingCompliance
} from './types';
import { makeApiCall, handleApiError } from './utils';

// PROJECT HOOKS

export const useProjects = (baseUrl: string, organization: string) => {
  const [projects, setProjects] = useState<{ label: string; value: string }[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchProjects = async (urlBase: string = baseUrl) => {
    setLoading(true);
    setError(null);
    
    try {
      const url = `${urlBase}/api/ado/${organization}/_apis/projects?api-version=7.0`;
      console.log('[FRONTEND] Fetching projects from:', url);

      const data = await makeApiCall(url);
      console.log('[FRONTEND] Projects fetched successfully:', data);

      if (!data.value || !Array.isArray(data.value)) {
        throw new Error('Invalid projects response format - missing or invalid "value" field');
      }

      const projectOptions = data.value.map((project: any) => ({
        label: project.name,
        value: project.name
      }));

      projectOptions.sort((a: { label: string; value: string }, b: { label: string; value: string }) => 
        a.label.localeCompare(b.label)
      );

      setProjects(projectOptions);
      console.log('[FRONTEND] Available projects:', projectOptions.map((p: { label: string; value: string }) => p.label));

    } catch (err: any) {
      const errorMessage = handleApiError(err, 'projects');
      setError(errorMessage);
      
      console.log('[FRONTEND] Falling back to hardcoded projects');
      setProjects([
        { label: 'DevOps', value: 'DevOps' },
        { label: 'Testing', value: 'Testing' },
      ]);
    } finally {
      setLoading(false);
    }
  };

  return { projects, loading, error, fetchProjects };
};


// REPOSITORY HOOKS


export const useRepositories = (baseUrl: string, organization: string) => {
  const [repos, setRepos] = useState<Repository[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchRepositories = async (project: string) => {
    setLoading(true);
    setRepos([]);
    setError(null);

    try {
      const url = `${baseUrl}/api/ado/${organization}/${encodeURIComponent(project)}/_apis/git/repositories?api-version=7.0`;
      console.log('[FRONTEND] Making request to:', url);

      const data = await makeApiCall(url);
      console.log('[FRONTEND] Repositories found:', data.value?.length || 0);

      if (!data.value || !Array.isArray(data.value)) {
        throw new Error('Invalid repositories response format - missing or invalid "value" field');
      }

      setRepos(data.value);
    } catch (err: any) {
      const errorMessage = handleApiError(err, 'repositories');
      setError(errorMessage);
      setRepos([]);
    } finally {
      setLoading(false);
    }
  };

  return { repos, loading, error, fetchRepositories };
};

export const useRepositoryHealth = (baseUrl: string, organization: string) => {
  const [repoHealthDetails, setRepoHealthDetails] = useState<RepositoryHealthDetails | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchRepositoryDetails = async (project: string, repository: Repository) => {
    setLoading(true);
    setError(null);

    try {
      const url = `${baseUrl}/api/ado/${organization}/${encodeURIComponent(project)}/_apis/git/repositories/${repository.id}/details?api-version=7.0`;
      console.log('[FRONTEND] Fetching repository details from:', url);

      const data = await makeApiCall(url);
      console.log('[FRONTEND] Repository details fetched successfully:', data);

      setRepoHealthDetails(data);
    } catch (err: any) {
      const errorMessage = handleApiError(err, 'repository details');
      setError(errorMessage);
      setRepoHealthDetails(null);
    } finally {
      setLoading(false);
    }
  };

  return { repoHealthDetails, loading, error, fetchRepositoryDetails, setRepoHealthDetails };
};


// PIPELINE HOOKS


export const usePipelineData = (baseUrl: string, organization: string) => {
  const [pipelineData, setPipelineData] = useState<ProjectPipelineData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchPipelineData = async (project: string) => {
    setLoading(true);
    setError(null);
    
    try {
      const url = `${baseUrl}/api/ado/${organization}/${encodeURIComponent(project)}/_apis/pipelines/overview`;
      console.log('[FRONTEND] Fetching pipeline data from:', url);

      const data = await makeApiCall(url);
      console.log('[FRONTEND] Pipeline data fetched successfully:', data);

      setPipelineData(data);
    } catch (err: any) {
      const errorMessage = handleApiError(err, 'pipeline data');
      setError(errorMessage);
      setPipelineData(null);
    } finally {
      setLoading(false);
    }
  };

  return { pipelineData, loading, error, fetchPipelineData, setPipelineData };
};


// RELEASE HOOKS


export const useReleaseData = (baseUrl: string, organization: string) => {
  const [releaseData, setReleaseData] = useState<ProjectReleaseData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchReleaseData = async (project: string) => {
    setLoading(true);
    setError(null);
    
    try {
      const url = `${baseUrl}/api/ado/${organization}/${encodeURIComponent(project)}/_apis/releases/overview`;
      console.log('[FRONTEND] Fetching OPTIMIZED release data from:', url);
      console.log('[FRONTEND] Organization:', organization);
      console.log('[FRONTEND] Project:', project);

      // Add timeout to prevent hanging
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout

      const response = await fetch(url, {
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      console.log('[FRONTEND] Release API response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[FRONTEND] Release data error response:', errorText);
        throw new Error(`Failed to fetch release data: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      console.log('[FRONTEND] OPTIMIZED Release data fetched successfully:', {
        totalReleases: data.totalReleases,
        totalDefinitions: data.totalReleaseDefinitions,
        activeReleases: data.activeReleases,
        loadedReleases: data.releases?.length || 0,
        loadedDefinitions: data.releaseDefinitions?.length || 0,
        hasEnvironmentStats: data.environmentStats?.length > 0
      });

      setReleaseData(data);
    } catch (err: any) {
      console.error('[FRONTEND] Error fetching OPTIMIZED release data:', err);
      
      if (err.name === 'AbortError') {
        setError(`Request timeout - Release data loading took too long. This may indicate a large dataset.`);
      } else {
        setError(`Failed to load release data: ${err.message}`);
      }
      setReleaseData(null);
    } finally {
      setLoading(false);
    }
  };

  return { releaseData, loading, error, fetchReleaseData, setReleaseData };
};


// BRANCH POLICY HOOKS


export const usePolicyReport = (baseUrl: string, organization: string) => {
  const [policyReport, setPolicyReport] = useState<ProjectBranchPolicyReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchPolicyReport = async (project: string) => {
    setLoading(true);
    setError(null);
    
    try {
      const url = `${baseUrl}/api/ado/${organization}/${encodeURIComponent(project)}/_apis/policies/report`;
      console.log('[FRONTEND] Fetching branch policy report from:', url);

      const data = await makeApiCall(url);
      console.log('[FRONTEND] Policy report fetched successfully:', data);

      setPolicyReport(data);
    } catch (err: any) {
      const errorMessage = handleApiError(err, 'policy report');
      setError(errorMessage);
      setPolicyReport(null);
    } finally {
      setLoading(false);
    }
  };

  return { policyReport, loading, error, fetchPolicyReport, setPolicyReport };
};


// DEPLOYMENT HOOKS


export const useDeploymentData = (baseUrl: string, organization: string) => {
  const [deploymentData, setDeploymentData] = useState<FilteredProjectDeploymentData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchDeploymentData = async (project: string) => {
    setLoading(true);
    setError(null);
    
    try {
      const url = `${baseUrl}/api/ado/${organization}/${encodeURIComponent(project)}/_apis/deployments/overview`;
      console.log('[FRONTEND] Fetching deployment data from:', url);

      const data = await makeApiCall(url);
      console.log('[FRONTEND] Deployment data fetched successfully:', data);

      setDeploymentData(data);
    } catch (err: any) {
      const errorMessage = handleApiError(err, 'deployment data');
      setError(errorMessage);
      setDeploymentData(null);
    } finally {
      setLoading(false);
    }
  };

  return { deploymentData, loading, error, fetchDeploymentData, setDeploymentData };
};


// BRANCH NAMING HOOKS


export const useBranchNamingReport = (baseUrl: string, organization: string) => {
  const [namingReport, setNamingReport] = useState<ProjectBranchNamingReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchBranchNamingReport = async (project: string) => {
    setLoading(true);
    setError(null);
    
    try {
      const url = `${baseUrl}/api/ado/${organization}/${encodeURIComponent(project)}/_apis/branch-naming/report`;
      console.log('[FRONTEND] Fetching branch naming report from:', url);

      const data = await makeApiCall(url);
      console.log('[FRONTEND] Branch naming report fetched successfully:', data);

      setNamingReport(data);
    } catch (err: any) {
      const errorMessage = handleApiError(err, 'branch naming report');
      setError(errorMessage);
      setNamingReport(null);
    } finally {
      setLoading(false);
    }
  };

  return { namingReport, loading, error, fetchBranchNamingReport, setNamingReport };
};


// SELECTION STATE HOOKS


export const useSelectedItems = () => {
  const [selectedRepository, setSelectedRepository] = useState<Repository | null>(null);
  const [selectedPipeline, setSelectedPipeline] = useState<PipelineInfo | null>(null);
  const [selectedRelease, setSelectedRelease] = useState<Release | null>(null);
  const [selectedPolicyRepository, setSelectedPolicyRepository] = useState<BranchPolicyCompliance | null>(null);
  const [selectedDeployment, setSelectedDeployment] = useState<ProcessedDeployment | null>(null);
  const [selectedNamingRepository, setSelectedNamingRepository] = useState<BranchNamingCompliance | null>(null);

  const clearAllSelections = () => {
    setSelectedRepository(null);
    setSelectedPipeline(null);
    setSelectedRelease(null);
    setSelectedPolicyRepository(null);
    setSelectedDeployment(null);
    setSelectedNamingRepository(null);
  };

  const clearRepositorySelection = () => {
    setSelectedRepository(null);
  };

  const clearPipelineSelection = () => {
    setSelectedPipeline(null);
  };

  const clearReleaseSelection = () => {
    setSelectedRelease(null);
  };

  const clearPolicySelection = () => {
    setSelectedPolicyRepository(null);
  };

  const clearDeploymentSelection = () => {
    setSelectedDeployment(null);
  };

  const clearNamingSelection = () => {
    setSelectedNamingRepository(null);
  };

  return {
    // Selected items
    selectedRepository,
    setSelectedRepository,
    selectedPipeline,
    setSelectedPipeline,
    selectedRelease,
    setSelectedRelease,
    selectedPolicyRepository,
    setSelectedPolicyRepository,
    selectedDeployment,
    setSelectedDeployment,
    selectedNamingRepository,
    setSelectedNamingRepository,
    
    // Clear functions
    clearAllSelections,
    clearRepositorySelection,
    clearPipelineSelection,
    clearReleaseSelection,
    clearPolicySelection,
    clearDeploymentSelection,
    clearNamingSelection
  };
};


// NAVIGATION HOOKS


export const useNavigation = () => {
  const [viewMode, setViewMode] = useState<'initial' | 'repositories' | 'pipelines' | 'releases' | 'policies' | 'deployments' | 'branch-naming'>('initial');
  const [tabValue, setTabValue] = useState(0);
  const [debugInfo, setDebugInfo] = useState<string>('');

  const handleViewModeChange = (mode: typeof viewMode) => {
    setViewMode(mode);
    setTabValue(0);
  };

  const handleTabChange = (_: React.ChangeEvent<{}>, newValue: number) => {
    setTabValue(newValue);
  };

  const resetNavigation = () => {
    setViewMode('initial');
    setTabValue(0);
  };

  const updateDebugInfo = (info: string) => {
    setDebugInfo(info);
  };

  return {
    viewMode,
    setViewMode,
    tabValue,
    setTabValue,
    debugInfo,
    setDebugInfo,
    handleViewModeChange,
    handleTabChange,
    resetNavigation,
    updateDebugInfo
  };
};


// DATA LOADING HOOKS


export const useProjectDataLoader = (
  baseUrl: string,
  organization: string,
  selectedProject: string
) => {
  const [isLoading, setIsLoading] = useState(false);
  const [loadingStage, setLoadingStage] = useState<string>('');

  // Get all individual hooks
  const { fetchRepositories } = useRepositories(baseUrl, organization);
  const { fetchPipelineData } = usePipelineData(baseUrl, organization);
  const { fetchReleaseData } = useReleaseData(baseUrl, organization);
  const { fetchPolicyReport } = usePolicyReport(baseUrl, organization);
  const { fetchDeploymentData } = useDeploymentData(baseUrl, organization);
  const { fetchBranchNamingReport } = useBranchNamingReport(baseUrl, organization);

  const loadAllProjectData = async (project: string) => {
    setIsLoading(true);
    
    try {
      setLoadingStage('Loading repositories...');
      await fetchRepositories(project);
      
      setLoadingStage('Loading pipelines...');
      await fetchPipelineData(project);
      
      setLoadingStage('Loading releases...');
      await fetchReleaseData(project);
      
      setLoadingStage('Loading branch policies...');
      await fetchPolicyReport(project);
      
      setLoadingStage('Loading deployments...');
      await fetchDeploymentData(project);
      
      setLoadingStage('Loading branch naming standards...');
      await fetchBranchNamingReport(project);
      
      setLoadingStage('Complete!');
      
    } catch (error) {
      console.error('[FRONTEND] Error loading project data:', error);
      setLoadingStage(`Error: ${error}`);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    isLoading,
    loadingStage,
    loadAllProjectData
  };
};


// ERROR HANDLING HOOKS


export const useErrorHandler = () => {
  const [errors, setErrors] = useState<Record<string, string | null>>({
    projects: null,
    repositories: null,
    pipelines: null,
    releases: null,
    policies: null,
    deployments: null,
    branchNaming: null
  });

  const setError = (section: keyof typeof errors, error: string | null) => {
    setErrors(prev => ({
      ...prev,
      [section]: error
    }));
  };

  const clearError = (section: keyof typeof errors) => {
    setErrors(prev => ({
      ...prev,
      [section]: null
    }));
  };

  const clearAllErrors = () => {
    setErrors({
      projects: null,
      repositories: null,
      pipelines: null,
      releases: null,
      policies: null,
      deployments: null,
      branchNaming: null
    });
  };

  const hasErrors = Object.values(errors).some(error => error !== null);
  const errorCount = Object.values(errors).filter(error => error !== null).length;

  return {
    errors,
    setError,
    clearError,
    clearAllErrors,
    hasErrors,
    errorCount
  };
};


// LOADING STATE HOOKS


export const useLoadingStates = () => {
  const [loadingStates, setLoadingStates] = useState<Record<string, boolean>>({
    projects: false,
    repositories: false,
    pipelines: false,
    releases: false,
    policies: false,
    deployments: false,
    branchNaming: false
  });

  const setLoading = (section: keyof typeof loadingStates, isLoading: boolean) => {
    setLoadingStates(prev => ({
      ...prev,
      [section]: isLoading
    }));
  };

  const isAnyLoading = Object.values(loadingStates).some(loading => loading);
  const loadingCount = Object.values(loadingStates).filter(loading => loading).length;

  return {
    loadingStates,
    setLoading,
    isAnyLoading,
    loadingCount
  };
};